import { useMemo } from 'react';

import { useCart } from '../useCart';
import { useGroupOrderDetailsQuery } from './GraphQL/GroupOrderDetails.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderDetails = () => {
  const { cart, cartOrderType } = useCart();

  const joinCode = cart?.multipartyDetails?.joinCode ?? '';
  const canSubmitOrder = cart?.currentUserOrderPermissions.canSubmitOrder === true;

  const isGroupOrder = Boolean(joinCode);
  const isGroupOrderHost = isGroupOrder && canSubmitOrder;
  const isGroupOrderInvitee = isGroupOrder && !isGroupOrderHost;

  // ───────────────────────────────────────────────────────────────────────────

  const [previewResponse] = useGroupOrderDetailsQuery({
    variables: { joinCode },
    pause: !isGroupOrderInvitee,
    requestPolicy: 'cache-first',
  });

  // ─── Derived ────────────────────────────────────────────────────────────────

  const preview = previewResponse.data?.multipartyOrderPreview;
  const isPreviewSuccessful = preview?.__typename === 'MultipartyOrderPreviewSuccess';
  const hostDisplayName = isPreviewSuccessful ? preview.hostDisplayName : undefined;

  const hostFirstName = useMemo(() => {
    const firstName = hostDisplayName?.trim().split(/\s+/)[0];

    if (!firstName) return;

    return firstName;
  }, [hostDisplayName]);

  const deliveryStreet = cart?.deliveryOrderDetail?.address?.street;
  const restaurantName = cart?.restaurant?.name;
  const locationName = cartOrderType === 'delivery' ? deliveryStreet : restaurantName;

  const hostGroupOrderDetails = useMemo(() => {
    if (!isGroupOrderHost || !locationName) return;

    return { locationName, orderChannel: cartOrderType };
  }, [cartOrderType, isGroupOrderHost, locationName]);

  const inviteeGroupOrderDetails = useMemo(() => {
    if (!isGroupOrderInvitee || !hostFirstName) return;

    return { hostFirstName };
  }, [hostFirstName, isGroupOrderInvitee]);

  return {
    hostGroupOrderDetails,
    inviteeGroupOrderDetails,
  };
};
